import { Fragment, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setFormValue } from "../../redux/slices/securitygroupspageslice";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Checkbox } from "@progress/kendo-react-inputs";
import { FormInput, FormTextArea } from "../../forms/form-components";
import { FormRegion } from "../../components/formregion";
import { formValidator } from "./securitygroupspageformvalidator";
import { List } from "../../components/list";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { useTranslation } from "react-i18next";

const usersItemRenderer =
  (currentTheme, onUserCheck) => (props, item, index) => {
    return (
      <div key={item.Id} className={`list-item`}>
        <div style={{ width: 28 }}>
          <Checkbox value={item.Checked} onChange={(e) => onUserCheck(item)} />
        </div>
        <div className="list-item-name">{item.Name}</div>
      </div>
    );
  };

const reportsItemRenderer =
  (currentTheme, onReportCheck) => (props, item, index) => {
    return (
      <div key={item.Id} className={`list-item`}>
        <div style={{ width: 28 }}>
          <Checkbox
            value={item.Checked}
            onChange={(e) => onReportCheck(item)}
          />
        </div>
        <div className="list-item-name">{item.Title}</div>
      </div>
    );
  };

export const ClaimsGrid = ({ claims = [], onToggleClaim, t, disabled = false }) => {
  // Separate mapped and unmapped
  const mappedClaims = claims.filter(
    (groupObj) => groupObj.Feature !== "notMapped"
  );

  const noFeatureMapping = claims.filter(
    (groupObj) => groupObj.Feature === "notMapped"
  );

  // Build set of all action names present in mapped claims
  const actionSet = new Set();
  mappedClaims.forEach((groupObj) => {
    if (!groupObj) return;
    if (Array.isArray(groupObj.Claims)) {
      groupObj.Claims.forEach((p) => {
        const name = p.Action || p.Claim;
        if (name) actionSet.add(name);
      });
    } else {
      const key = Object.keys(groupObj)[0];
      const perms = groupObj[key];
      if (Array.isArray(perms)) {
        perms.forEach((p) => {
          const name = p.Action || p.Claim;
          if (name) actionSet.add(name);
        });
      }
    }
  });

  const actions = Array.from(actionSet);

  // Build rows for the grid from mappedClaims only
  const rows = mappedClaims
    .map((groupObj) => {
      if (!groupObj) return null;

      let featureName;
      let perms = [];

      if (Array.isArray(groupObj.Claims)) {
        featureName = groupObj.Feature;
        perms = groupObj.Claims;
      } else {
        featureName = Object.keys(groupObj)[0];
        perms = Array.isArray(groupObj[featureName])
          ? groupObj[featureName]
          : [];
      }

      const row = { Claim: featureName || "" };

      actions.forEach((action) => {
        const match = perms.find(
          (p) => p.Action === action || p.Claim === action
        );
        row[action] = match
          ? match.Allowed ?? match.ClaimValue ?? false
          : false;

        if (match && match.ClaimKey) {
          row[`${action}_key`] = match.ClaimKey;
        }
      });

      // Add "allSelected" property for the new "All" column
      row.allSelected = actions.every((action) => row[action]);

      return row;
    })
    .filter(Boolean);

  return (
    <div>
      <Grid data={rows}>
        <GridColumn field="Claim" title={t("securitygroups.feature")} />

        {actions.map((action) => (
          <GridColumn
            key={action}
            field={action}
            title={action}
            cell={(props) => (
              <td style={{ textAlign: "center" }}>
                <Checkbox
                  checked={!!props.dataItem[action]}
                  disabled={disabled}
                  onChange={() => onToggleClaim?.(props.dataItem.Claim, action)}
                />
              </td>
            )}
          />
        ))}

        {/* New All column */}
        <GridColumn
          field="allSelected"
          title={t("securitygroups.all")}
          cell={(props) => (
            <td style={{ textAlign: "center" }}>
              <Checkbox
                checked={props.dataItem.allSelected}
                disabled={disabled}
                onChange={() => {
                  actions.forEach((action) =>
                    onToggleClaim(
                      props.dataItem.Claim,
                      action,
                      !props.dataItem.allSelected
                    )
                  );
                }}
              />
            </td>
          )}
        />
      </Grid>

      {/* Render notMapped outside the grid */}
      {noFeatureMapping && (
        <div style={{ marginTop: 20 }}>
          <div style={{ marginTop: 32, marginBottom: 12 }}>
            {t("securitygroups.otherperm")}
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            {noFeatureMapping[0].Claims.map((perm, index) => {
              const key = perm.ClaimKey;
              const value = perm.ClaimValue ?? false;
              return (
                <div
                  key={key}
                  style={{
                    flex: "0 0 30%",
                    marginBottom: 6,
                  }}
                >
                  <Checkbox
                    checked={value}
                    label={key}
                    disabled={disabled}
                    onChange={() => onToggleClaim("notMapped", key)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export const SecurityGroupsPageForm = (props) => {
  const dispatch = useDispatch();
  const currentTheme = useSelector((state) => state.currentTheme);
  const pageState = useSelector((state) => state.securityGroupsPage);
  const updateDirtyState = props.updateDirtyState;
  const currentUser = useSelector((state) => state.currentUser);

  const canViewSectionReport = pageState?.formValues?.Claims?.some(
    (f) =>
      f?.Feature === "Reports" &&
      Array.isArray(f.Claims) &&
      f.Claims.some((c) => c.Claim === "View" && c.ClaimValue === true)
  );

  const { t } = useTranslation();
  //handle is dirty
  useEffect(() => {
    updateDirtyState();
  }, [pageState.formInitialValues, pageState.formValues]);

  const isModel =
    currentUser &&
    currentUser.IsAuthenticated &&
    currentUser.Subscription &&
    currentUser.Subscription.Name.toLowerCase() == "model";

  return (
    <Fragment>
      <Form
        validator={formValidator(pageState.mode, t)}
        key={pageState.formKey}
        initialValues={{ ...pageState.formInitialValues }}
        render={(formRenderProps) => {
          return (
            <FormElement
              horizontal={false}
              style={
                {
                  // width: 250,
                }
              }
            >
              <FormRegion className="form-region-1">
                <div className="form-group form-group-1">
                  <div style={{ flex: 1 }}>
                    <Field
                      id={"Name"}
                      name={"Name"}
                      label={t("securitygroups.name")}
                      component={FormInput}
                      onChange={(e) =>
                        dispatch(setFormValue({ id: "Name", value: e.value }))
                      }
                      autoFocus={true}
                      maxLength={50}
                    />
                  </div>
                  <Field
                    id={"Description"}
                    name={"Description"}
                    label={t("securitygroups.description")}
                    component={FormTextArea}
                    style={{ height: 50 }}
                    onChange={(e) =>
                      dispatch(
                        setFormValue({ id: "Description", value: e.value })
                      )
                    }
                    maxLength={255}
                  />
                  <div style={{ marginTop: 32, marginBottom: 6 }}>
                    {t("securitygroups.permissions")}
                  </div>
                  <div
                    style={{
                      marginBottom: 12,
                      display: "flex",
                      justifyItems: "right",
                    }}
                  >
                    <Checkbox
                      checked={pageState?.formValues?.Claims?.every(
                        (groupObj) => {
                          if (!groupObj) return true;
                          if (
                            groupObj.Feature &&
                            Array.isArray(groupObj.Claims)
                          ) {
                            return groupObj.Claims.every(
                              (p) => p.ClaimValue || p.Allowed
                            );
                          } else {
                            const key = Object.keys(groupObj)[0];
                            return groupObj[key]?.every(
                              (p) => p.ClaimValue || p.Allowed
                            );
                          }
                        }
                      )}
                      label={t("securitygroups.select_all")}
                      onChange={(e) => props.onSelectAllClaims(e.value)}
                    />
                  </div>
                  <div
                    style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}
                  >
                    <ClaimsGrid
                      claims={pageState?.formValues?.Claims || []}
                      onToggleClaim={props.onToggleClaim}
                      t={t}
                    />
                  </div>

                  <div
                    style={{
                      marginTop: 32,
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 13,
                        color: currentTheme.inputLabelColor,
                      }}
                    >
                      {t("securitygroups.users")}
                    </div>
                    <div style={{ flex: 1, marginTop: 2 }}>
                      <List
                        className="users"
                        items={pageState.formValues.Users}
                        itemRenderer={usersItemRenderer(
                          currentTheme,
                          props.onUserCheck
                        )}
                      />
                    </div>
                  </div>
                  {canViewSectionReport && (
                    <div
                      style={{
                        marginTop: 32,
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 13,
                          color: currentTheme.inputLabelColor,
                        }}
                      >
                        {t("securitygroups.reports")}
                      </div>

                      <div style={{ marginBottom: 10, marginTop: 15 }}>
                        <Checkbox
                          label={t("securitygroups.select_all_reports")}
                          checked={pageState.formValues.Reports.every(
                            (r) => r.Checked
                          )}
                          onChange={(e) => props.onToggleAllReports(e.value)}
                        />
                      </div>
                      <div style={{ flex: 1, marginTop: 2 }}>
                        <List
                          className="reports"
                          items={pageState.formValues.Reports}
                          itemRenderer={reportsItemRenderer(
                            currentTheme,
                            props.onReportCheck
                          )}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </FormRegion>
            </FormElement>
          );
        }}
      />
      <style>
        {`        
                    .securitygroups-page-form .k-form
                    {          
                        /*the kendo form which is child of my form.content*/     
                        width: 100%;
                        display: flex;
                        flex: 1;
                        justify-content: space-between;
                        padding: 0 24px 12px 24px;/*keep 12px in right and bottom so that when minimizing we keep a white region to the right and bottom; also keep 8px on the left*/
                    }

                    .securitygroups-page-form .form-region-1
                    {
                        width: 100%;
                        max-width: 1000px;
                        margin-top: 20px !important;/*to override kendo first child of k-form having marginTop=0*/                     
                    }

                    .securitygroups-page-form .form-region-1 .form-group-1
                    {
                        display: flex;
                        flex-direction: column;
                        width: 100%;
                    }
                   
                    .securitygroups-page-form > .content
                    {
                        padding: 0px;
                        background-color: transparent;
                        align-items: flex-start;
                    }

                    .securitygroups-page-form .form-region-root
                    {
                        background-color: transparent;
                    }

                    .securitygroups-page-form input.k-input, .securitygroups-page-form textarea.k-textarea, 
                    .securitygroups-page-form .k-dropdownlist .k-input-inner
                    {
                        /*make input controls have a bit more height*/
                        padding: 6px 8px;
                    }

                    .securitygroups-page-form .form-region-root .list-container.users
                    {
                        // border: 1px solid ${currentTheme.componentBorderColor};
                        border-radius: 4px;
                        margin-top: 2px;
                        flex: 1;
                        max-height: 160px;
                    }

                    .securitygroups-page-form .form-region-root .list-container.users .list-root
                    {
                        display: flex !important;
                        flex-wrap: wrap;   
                    }

                    .securitygroups-page-form .form-region-root .list-container.users .list-item
                    {
                        display: flex !important;
                        width: 50%;
                        overflow-x: hidden;
                    }


                    .securitygroups-page-form .form-region-root .list-container.reports
                    {
                        border: 1px solid ${currentTheme.componentBorderColor};
                        border-radius: 4px;
                        margin-top: 2px;
                        flex: 1;
                        max-height: 220px;
                    }

                    .securitygroups-page-form .form-region-root .list-container.reports .list-root
                    {
                        display: flex !important;
                        flex-wrap: wrap;   
                    }

                    .securitygroups-page-form .form-region-root .list-container.reports .list-item
                    {
                        display: flex !important;
                        width: 50%;
                        overflow-x: hidden;
                    }

                    .securitygroups-page-form  .k-grid-header .k-header{
                       text-align:center;
                      }
                       
                    .securitygroups-page-form .k-grid-header .k-header .k-link {
                      justify-content: center; /* center the header text horizontally */
                      display: flex;           /* make flex so justify-content works */
                    }
                `}
      </style>
    </Fragment>
  );
};
